import { Link } from 'react-router-dom'
import { useHomeDashboard } from '@/api/queries'
import { StatusChip } from '@/components/ui/status-chip'
import type { Status } from '@/components/ui/status-chip'
import type { WidgetSpan } from '../Widget'
import { Widget } from '../Widget'

const SEV_STATUS: Record<string, Status> = { SEV1: 'critical', SEV2: 'critical', SEV3: 'warn', SEV4: 'info' }

/**
 * Active-incident posture — REAL, from the leadership-composed snapshot
 * (counts by severity; the per-incident war-room rows live in the Incidents
 * module). Zero active incidents renders as an honest "all clear", never a
 * fabricated sample list.
 */
export function IncidentsWidget({ span = 4 }: { span?: WidgetSpan }) {
  const { data, isLoading } = useHomeDashboard()
  const posture = data?.incidents
  const severities = posture ? Object.entries(posture.bySeverity).sort(([a], [b]) => a.localeCompare(b)) : []

  return (
    <Widget
      title="Active incidents"
      provenance={data?.provenance.incidents}
      span={span}
      footer={
        <Link to="/incidents" className="font-semibold text-accent hover:underline">
          Open incidents module →
        </Link>
      }
    >
      {isLoading || !posture ? (
        <div className="text-[12.5px] text-ink-2">Loading…</div>
      ) : posture.total === 0 ? (
        <div className="py-2 text-[12.5px] text-ink-2">
          <span className="font-semibold text-ok">0 active incidents</span> — no non-resolved incidents visible to you.
        </div>
      ) : (
        <div>
          <div className="mb-2 text-[26px] font-bold tracking-tight">{posture.total}</div>
          <div className="flex flex-wrap gap-1.5">
            {severities.map(([sev, count]) => (
              <StatusChip key={sev} status={SEV_STATUS[sev] ?? 'unknown'}>
                {sev} · {count}
              </StatusChip>
            ))}
          </div>
        </div>
      )}
    </Widget>
  )
}
